const WS_LIVE_URL = window.process.env.WS_LIVE_URL || "";

// Map to store live sockets
// socketsMap = { [liveId]: WebSocket }
let socketsMap = {};

export async function generateSignedUrl(url, body = null) {
  const encoder = new TextEncoder();
  const secretKeyData = encoder.encode("2qySfsbI5akSqHmj_empreinte_bNLt1pnS4I");
  const key = await crypto.subtle.importKey(
    "raw",
    secretKeyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const expiry = Date.now() + 3 * 60 * 1000; // 3min
  const dataToAuthenticate = `${url.pathname}@${expiry}`;
  const mac = await crypto.subtle.sign("HMAC", key, encoder.encode(dataToAuthenticate));
  const base64Mac = btoa(String.fromCharCode(...new Uint8Array(mac)));

  url.searchParams.set("mac", base64Mac);
  url.searchParams.set("expiry", expiry.toString());

  return url.toString();
}

const state = () => ({
  currentLive: null,
  socketsMap: {}, // keep multiple sockets here
  reloadListFlag: false, // flag to trigger list reload
});

const getters = {
  getCurrentLive: (state) => state.currentLive,
  getSocketsMap: (state) => state.socketsMap,
  getSocketById: (state) => (id) => state.socketsMap[id] || null,
  getReloadListFlag: (state) => state.reloadListFlag,
};

const mutations = {
  SET_SOCKET(state, { id, socket }) {
    state.socketsMap = { ...state.socketsMap, [id]: socket };
    socketsMap[id] = socket; // keep global reference
  },
  REMOVE_SOCKET(state, id) {
    const { [id]: removed, ...rest } = state.socketsMap;
    state.socketsMap = rest;
    delete socketsMap[id];
  },
  UPDATE_LIVE_STATUS(state, { id, status }) {
    if (state.currentLive.id === id) {
      state.currentLive.status = status;
    }
  },
  TRIGGER_RELOAD_LIST(state) {
    state.reloadListFlag = !state.reloadListFlag; // toggle to trigger watchers
  },

};

const actions = {
  async initSocketLive({ commit, getters }, id) {
    if (getters.getSocketById(id)) return;

    const connectWebSocket = async () => {
      let wsUrl = WS_LIVE_URL;
      if (!wsUrl.includes("wss://")) wsUrl = "wss:" + wsUrl;
      wsUrl += `/?room=${id}&role=participant`;

      const signedWsUrl = await generateSignedUrl(new URL(wsUrl));
      const socket = new WebSocket(signedWsUrl);
      let rejoined = false;
      const startTime = Date.now();

      const rejoin = async () => {
        if (!rejoined) {
          rejoined = true;
          const elapsed = Date.now() - startTime;
          if (elapsed < 1000) {
            await new Promise((resolve) => setTimeout(resolve, 1000 - elapsed));
          }
          connectWebSocket();
        }
      };

      socket.onopen = () => {
        commit("SET_SOCKET", { id, socket });
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if(data.type ==="live_ended" || data.type==="live_started"){
            commit("TRIGGER_RELOAD_LIST");

        }
      };

      socket.onerror = () => {
        socket.close();
        rejoin();
      };

      socket.onclose = () => {
        rejoin();
      };
    };

    connectWebSocket();
  },

  closeSocket({ commit, getters }, id) {
    const socket = getters.getSocketById(id);
    if (socket) {
      socket.close();
      commit("REMOVE_SOCKET", id);
    }
  },

  closeAllSockets({ state, commit }) {
    Object.keys(state.socketsMap).forEach((id) => {
      state.socketsMap[id].close();
      commit("REMOVE_SOCKET", id);
    });
  },

  async syncLivesSockets({ dispatch, getters }, { lives, diffusionType }) {
    if (!lives.length || diffusionType !== "endless") {
      dispatch("closeAllSockets");
      return;
    }

    const liveIds = lives.map((l) => l.id);

    // Close sockets for removed lives
    Object.keys(getters.getSocketsMap).forEach((id) => {
      if (!liveIds.includes(Number(id))) {
        dispatch("closeSocket", Number(id));
      }
    });

    // Create sockets for new lives
    for (const live of lives) {
      if (!getters.getSocketById(live.id)) {
        dispatch("initSocketLive", live.id);
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./src/store/modules/lives-socket.js